import React, { useState, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  getDashboardMetrics,
  getProfitLossReport,
  getSalesByStore,
  getExpensesByCategory,
  getPaymentMethodsReport,
  getUnitsSoldReport,
  getInventoryPurchases,
  getInventoryValuation,
} from '../api/client';
import { formatCurrency, getCategoryLabel } from '../utils/formatters';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import { useStores } from '../contexts/StoreContext';
import html2canvas from 'html2canvas';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const CHART_COLORS = [
  '#2563eb', '#16a34a', '#dc2626', '#d97706', '#7c3aed',
  '#0891b2', '#be185d', '#65a30d', '#9f1239', '#0f766e',
];

// ── helpers ──────────────────────────────────────────────────────────────────

const fmtUSD = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const fmtVES = (v, rate) =>
  new Intl.NumberFormat('es-VE', { style: 'currency', currency: 'VES', maximumFractionDigits: 0 }).format((v || 0) * (rate || 1));

const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;

const fmtNum = (v) => new Intl.NumberFormat('es-ES').format(v || 0);

function aggregatePaymentMethods(paymentData, year, month) {
  if (!paymentData?.stores) return [];
  const monthKey = `${year}-${String(month).padStart(2, '0')}`;
  const totals = {};
  Object.values(paymentData.stores).forEach((store) => {
    const monthData = store.months?.[monthKey];
    if (!monthData) return;
    Object.entries(monthData.payment_methods || {}).forEach(([method, d]) => {
      if (!totals[method]) totals[method] = { usd: 0, count: 0, label: '' };
      totals[method].usd += d.amount_usd || 0;
      totals[method].count += d.transaction_count || 0;
      totals[method].label =
        paymentData.payment_method_labels?.[method] || method;
    });
  });
  return Object.entries(totals)
    .map(([key, val]) => ({ method: key, ...val }))
    .filter((d) => d.usd > 0)
    .sort((a, b) => b.usd - a.usd);
}

// ── sub-components ────────────────────────────────────────────────────────────

const SectionHeader = ({ title, color = '#1e40af' }) => (
  <div
    style={{
      backgroundColor: color,
      color: '#fff',
      padding: '10px 18px',
      borderRadius: '6px 6px 0 0',
      fontSize: '15px',
      fontWeight: 'bold',
      letterSpacing: '0.02em',
    }}
  >
    {title}
  </div>
);

const SectionBody = ({ children, noPadding = false }) => (
  <div
    style={{
      border: '1px solid #d1d5db',
      borderTop: 'none',
      borderRadius: '0 0 6px 6px',
      padding: noPadding ? 0 : '20px',
      backgroundColor: '#fff',
      marginBottom: '28px',
    }}
  >
    {children}
  </div>
);

const MetricCard = ({ label, value, sub, color = '#2563eb', bg = '#eff6ff' }) => (
  <div
    style={{
      backgroundColor: bg,
      border: `2px solid ${color}`,
      borderRadius: '8px',
      padding: '14px 16px',
      flex: 1,
      minWidth: '150px',
    }}
  >
    <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </div>
    <div style={{ fontSize: '20px', fontWeight: 'bold', color, marginTop: '4px' }}>{value}</div>
    {sub && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>{sub}</div>}
  </div>
);

const Th = ({ children, align = 'left' }) => (
  <th
    style={{
      backgroundColor: '#f3f4f6',
      padding: '8px 12px',
      textAlign: align,
      fontSize: '12px',
      fontWeight: '600',
      color: '#374151',
      borderBottom: '2px solid #d1d5db',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </th>
);

const Td = ({ children, align = 'left', bold = false, color = '#111827' }) => (
  <td
    style={{
      padding: '7px 12px',
      fontSize: '13px',
      textAlign: align,
      fontWeight: bold ? '700' : '400',
      color,
      borderBottom: '1px solid #f3f4f6',
    }}
  >
    {children}
  </td>
);

// ── main component ────────────────────────────────────────────────────────────

const MonthlyReportPDF = () => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);
  const reportRef = useRef(null);
  const { exchangeRate } = useExchangeRate();
  const { stores } = useStores();
  const [selectedStore, setSelectedStore] = useState('');
  const selectedStoreName = selectedStore ? (stores.find((s) => s.id === selectedStore)?.name || '') : '';

  const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
  const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
  const endDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  const monthLabel = `${MONTHS[selectedMonth - 1]} ${selectedYear}`;

  const years = [];
  for (let y = 2023; y <= now.getFullYear() + 1; y++) years.push(y);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setReportData(null);
    try {
      const [metrics, profitLoss, salesByStore, expensesByCategory, paymentMethods, unitsSold, inventoryRaw, inventoryValuation] =
        await Promise.all([
          getDashboardMetrics(startDate, endDate, selectedStore || null),
          getProfitLossReport(startDate, endDate, selectedStore || null),
          getSalesByStore(startDate, endDate, selectedStore || null),
          getExpensesByCategory(startDate, endDate, selectedStore || null),
          getPaymentMethodsReport({ startDate, endDate, storeId: selectedStore || null }),
          getUnitsSoldReport(startDate, endDate, selectedStore || null),
          getInventoryPurchases({ startDate, endDate, storeId: selectedStore || null, limit: 10000 }),
          getInventoryValuation(endDate, selectedStore || null).catch(() => null),
        ]);
      setReportData({
        metrics,
        profitLoss,
        salesByStore,
        expensesByCategory,
        paymentMethods,
        unitsSold,
        inventoryRaw,
        inventoryValuation,
      });
    } catch (e) {
      setError('Error al cargar los datos: ' + (e.message || 'error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!reportRef.current) return;
    setGenerating(true);
    try {
      // Load jsPDF from CDN if not already loaded
      if (!window.jspdf) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      }
      // Give charts extra time to fully render
      await new Promise((res) => setTimeout(res, 500));

      const canvas = await html2canvas(reportRef.current, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 0,
      });

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();   // 595.28
      const pageH = pdf.internal.pageSize.getHeight();  // 841.89
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const imgH = (canvas.height / canvas.width) * pageW;

      let offset = 0;
      while (offset < imgH) {
        if (offset > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, -offset, pageW, imgH);
        offset += pageH;
      }
      const storeSlug = selectedStoreName ? `-${selectedStoreName.replace(/[^A-Za-z0-9]+/g, '_')}` : '';
      pdf.save(`Reporte-Mensual-Farmavida${storeSlug}-${MONTHS[selectedMonth - 1]}-${selectedYear}.pdf`);
    } catch (e) {
      console.error('PDF error:', e);
      alert('Error generando PDF: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  // ── derived data ─────────────────────────────────────────────────────────

  const d = reportData;
  const m = d?.metrics || {};
  const pl = d?.profitLoss || {};
  const revenue = pl.revenue?.total_sales ?? pl.revenue ?? m.total_sales ?? 0;
  const cogs = pl.cost_of_goods_sold ?? m.cost_of_goods_sold ?? 0;
  const grossProfit = pl.gross_profit ?? m.gross_profit ?? 0;
  const totalExpenses = pl.total_expenses ?? m.total_expenses ?? 0;
  const netProfit = pl.net_profit ?? m.net_profit ?? 0;
  const totalInventory = m.total_inventory_purchases ?? 0;
  const grossMargin = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
  const netMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;

  const salesChartData = (d?.salesByStore || []).map((s) => ({
    tienda: s.store_name?.length > 12 ? s.store_name.substring(0, 12) + '…' : s.store_name,
    Ventas: parseFloat((s.total_sales || 0).toFixed(2)),
    fullName: s.store_name,
  }));

  // Exclude non-operating categories from the pie chart (they distort the view)
  const EXCLUDE_FROM_PIE = ['pagos_proveedores', 'compras_inventario'];
  const expCatData = (d?.expensesByCategory || [])
    .filter((e) => e.total_amount > 0 && !EXCLUDE_FROM_PIE.includes(e.category))
    .map((e, i) => ({
      name: getCategoryLabel(e.category),
      value: parseFloat((e.total_amount || 0).toFixed(2)),
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));

  const payTotalsRaw = d ? aggregatePaymentMethods(d.paymentMethods, selectedYear, selectedMonth) : [];
  const payTotalsSum = payTotalsRaw.reduce((s, p) => s + (p.usd || 0), 0);
  const payUnclassified = revenue - payTotalsSum;
  const payTotals = payUnclassified > 0.01
    ? [...payTotalsRaw, { method: '__unclassified__', label: 'Sin clasificar (POS/Pago Móvil pendiente)', usd: payUnclassified, count: 0 }]
    : payTotalsRaw;
  const payGrandTotal = payTotals.reduce((s, p) => s + (p.usd || 0), 0);
  const payChartData = payTotalsRaw.slice(0, 10).map((p) => ({
    metodo: p.label?.length > 16 ? p.label.substring(0, 16) + '…' : p.label,
    Monto: parseFloat((p.usd || 0).toFixed(2)),
    fullLabel: p.label,
  }));

  const distData = [
    { name: 'Ganancia Neta', value: Math.max(0, netProfit), color: '#16a34a' },
    { name: 'Gastos Operativos', value: totalExpenses, color: '#dc2626' },
    { name: 'Compras Inventario', value: totalInventory, color: '#d97706' },
  ].filter((x) => x.value > 0);

  const barCompareData = [
    { name: 'Ventas', Valor: parseFloat(revenue.toFixed(2)), fill: '#2563eb' },
    { name: 'Gastos Op.', Valor: parseFloat(totalExpenses.toFixed(2)), fill: '#dc2626' },
    { name: 'Compras Inv.', Valor: parseFloat(totalInventory.toFixed(2)), fill: '#d97706' },
    { name: 'Ganancia Neta', Valor: parseFloat(Math.max(0, netProfit).toFixed(2)), fill: '#16a34a' },
  ];

  // Inventory units purchased (from raw records)
  const invByStore = {};
  (d?.inventoryRaw || []).forEach((p) => {
    const key = p.store_id || 'N/D';
    if (!invByStore[key]) invByStore[key] = { units: 0, cost: 0, count: 0, storeName: key };
    invByStore[key].units += p.quantity || 0;
    invByStore[key].cost += p.total_amount_usd || 0;
    invByStore[key].count += 1;
  });
  const invStoreRows = Object.values(invByStore);

  // Match store names from salesByStore
  const storeNameMap = {};
  (d?.salesByStore || []).forEach((s) => { storeNameMap[s.store_id] = s.store_name; });
  invStoreRows.forEach((r) => {
    if (storeNameMap[r.storeName]) r.storeName = storeNameMap[r.storeName];
  });

  const totalInvUnits = invStoreRows.reduce((s, r) => s + r.units, 0);

  // Valor del inventario (snapshot Tecnodroid más cercano al cierre del mes)
  const inventoryVal = d?.inventoryValuation;
  const invValTotals = inventoryVal?.totals || null;
  const invValRows = inventoryVal?.by_store || [];

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* ── Controls ─────────────────────────────────────────── */}
      <div className="card mb-6">
        <div className="card-header">
          <h2 className="card-title">Reporte Mensual PDF</h2>
          <p className="text-sm text-gray-500 mt-1">
            Selecciona el mes y genera un reporte completo en PDF con todas las métricas, gráficas y tablas.
          </p>
        </div>
        <div className="flex flex-wrap gap-4 items-end mt-4">
          <div>
            <label className="form-label">Mes</label>
            <select
              className="form-select"
              value={selectedMonth}
              onChange={(e) => { setSelectedMonth(Number(e.target.value)); setReportData(null); }}
            >
              {MONTHS.map((mn, i) => (
                <option key={i + 1} value={i + 1}>{mn}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Año</label>
            <select
              className="form-select"
              value={selectedYear}
              onChange={(e) => { setSelectedYear(Number(e.target.value)); setReportData(null); }}
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Sede</label>
            <select
              className="form-select"
              value={selectedStore}
              onChange={(e) => { setSelectedStore(e.target.value); setReportData(null); }}
              style={{ minWidth: '220px' }}
            >
              <option value="">Todas las sedes (consolidado)</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="btn-primary"
            style={{ minWidth: '160px' }}
          >
            {loading ? '⏳ Cargando datos…' : '📊 Generar Reporte'}
          </button>
          {d && (
            <button
              onClick={handleExportPDF}
              disabled={generating}
              className="btn-primary"
              style={{ backgroundColor: '#16a34a', minWidth: '180px' }}
            >
              {generating ? '⏳ Generando PDF…' : '📥 Descargar PDF'}
            </button>
          )}
        </div>
        {error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
        )}
        {loading && (
          <div className="mt-4 flex items-center gap-3 text-blue-700 text-sm">
            <div className="animate-spin h-5 w-5 border-2 border-blue-700 border-t-transparent rounded-full" />
            Consultando datos del mes de {monthLabel}…
          </div>
        )}
      </div>

      {/* ── Report Preview ────────────────────────────────────── */}
      {d && (
        <div style={{ overflowX: 'auto' }}>
          <div
            ref={reportRef}
            style={{
              width: '1200px',
              minWidth: '1200px',
              backgroundColor: '#ffffff',
              padding: '40px',
              fontFamily: 'Arial, Helvetica, sans-serif',
              color: '#111827',
            }}
          >
            {/* ══ PORTADA ══════════════════════════════════════════ */}
            <div
              style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                borderRadius: '12px',
                padding: '40px 48px',
                marginBottom: '32px',
                color: '#fff',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '13px', opacity: 0.8, marginBottom: '6px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Gestión Financiera
                  </div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
                    Reporte Mensual{selectedStoreName ? ` · ${selectedStoreName}` : ''}
                  </div>
                  <div style={{ fontSize: '22px', opacity: 0.9, fontWeight: '600' }}>{monthLabel}</div>
                  {!selectedStoreName && (
                    <div style={{ fontSize: '13px', opacity: 0.75, marginTop: '4px' }}>Consolidado de todas las sedes</div>
                  )}
                  <div style={{ fontSize: '13px', opacity: 0.7, marginTop: '12px' }}>
                    Período: {startDate} — {endDate}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <img
                    src="/farmavida_logo.png"
                    alt="Farmavida"
                    style={{ height: '70px', borderRadius: '8px', background: 'white', padding: '4px' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '8px' }}>
                    Generado: {new Date().toLocaleDateString('es-VE')}
                  </div>
                </div>
              </div>
            </div>

            {/* ══ SECCIÓN 1: RESUMEN EJECUTIVO ═══════════════════════ */}
            <SectionHeader title="1. Resumen Ejecutivo" color="#1e40af" />
            <SectionBody>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <MetricCard
                  label="Ventas Totales"
                  value={fmtUSD(revenue)}
                  sub={`Bs. ${fmtNum(Math.round(revenue * (exchangeRate || 1)))}`}
                  color="#2563eb"
                  bg="#eff6ff"
                />
                <MetricCard
                  label="Costo Mercancía Vendida"
                  value={fmtUSD(cogs)}
                  sub={`${fmtPct(revenue > 0 ? (cogs / revenue) * 100 : 0)} de las ventas`}
                  color="#7c3aed"
                  bg="#f5f3ff"
                />
                <MetricCard
                  label="Ganancia Bruta"
                  value={fmtUSD(grossProfit)}
                  sub={`Margen: ${fmtPct(grossMargin)}`}
                  color="#0891b2"
                  bg="#ecfeff"
                />
                <MetricCard
                  label="Gastos Operativos"
                  value={fmtUSD(totalExpenses)}
                  sub={`${fmtPct(revenue > 0 ? (totalExpenses / revenue) * 100 : 0)} de las ventas`}
                  color="#dc2626"
                  bg="#fef2f2"
                />
                <MetricCard
                  label="Ganancia Neta"
                  value={fmtUSD(netProfit)}
                  sub={`Margen: ${fmtPct(netMargin)}`}
                  color={netProfit >= 0 ? '#16a34a' : '#dc2626'}
                  bg={netProfit >= 0 ? '#f0fdf4' : '#fef2f2'}
                />
                <MetricCard
                  label="Compras de Inventario"
                  value={fmtUSD(totalInventory)}
                  sub={`${fmtNum(totalInvUnits)} unidades compradas`}
                  color="#d97706"
                  bg="#fffbeb"
                />
              </div>
              {/* Best store */}
              {!selectedStoreName && m.best_performing_store?.store_name && (
                <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '10px 14px', fontSize: '13px' }}>
                  🏆 <strong>Mejor tienda del mes:</strong> {m.best_performing_store.store_name} — {fmtUSD(m.best_performing_store.sales_amount)}
                </div>
              )}
            </SectionBody>

            {/* ══ SECCIÓN 2: ESTADO DE RESULTADOS ════════════════════ */}
            <SectionHeader title="2. Estado de Resultados (P&L)" color="#1e3a8a" />
            <SectionBody noPadding>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <td style={{ padding: '10px 16px', fontSize: '14px', fontWeight: '600', color: '#111827', width: '60%' }}>
                      (+) Ventas Netas
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: '14px', fontWeight: '700', color: '#2563eb', textAlign: 'right' }}>
                      {fmtUSD(revenue)}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: '12px', color: '#6b7280', textAlign: 'right' }}>100%</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '8px 16px', fontSize: '13px', color: '#374151' }}>
                      &nbsp;&nbsp;(−) Costo de Mercancía Vendida (COGS)
                    </td>
                    <td style={{ padding: '8px 16px', fontSize: '13px', color: '#dc2626', textAlign: 'right' }}>
                      ({fmtUSD(cogs)})
                    </td>
                    <td style={{ padding: '8px 16px', fontSize: '12px', color: '#6b7280', textAlign: 'right' }}>
                      {fmtPct(revenue > 0 ? (cogs / revenue) * 100 : 0)}
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: '#f0fdf4', borderTop: '2px solid #d1fae5', borderBottom: '2px solid #d1fae5' }}>
                    <td style={{ padding: '10px 16px', fontSize: '14px', fontWeight: '700', color: '#111827' }}>
                      = GANANCIA BRUTA
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: '14px', fontWeight: '700', color: '#16a34a', textAlign: 'right' }}>
                      {fmtUSD(grossProfit)}
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: '12px', color: '#16a34a', textAlign: 'right', fontWeight: '600' }}>
                      {fmtPct(grossMargin)}
                    </td>
                  </tr>
                  {/* Operating expenses breakdown */}
                  {Object.entries(pl.expenses || {}).map(([cat, amt]) => (
                    <tr key={cat}>
                      <td style={{ padding: '6px 16px', fontSize: '13px', color: '#374151' }}>
                        &nbsp;&nbsp;(−) {getCategoryLabel(cat)}
                      </td>
                      <td style={{ padding: '6px 16px', fontSize: '13px', color: '#dc2626', textAlign: 'right' }}>
                        ({fmtUSD(amt)})
                      </td>
                      <td style={{ padding: '6px 16px', fontSize: '12px', color: '#6b7280', textAlign: 'right' }}>
                        {fmtPct(revenue > 0 ? (amt / revenue) * 100 : 0)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 16px', fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                      &nbsp;&nbsp;Total Gastos Operativos
                    </td>
                    <td style={{ padding: '8px 16px', fontSize: '13px', fontWeight: '600', color: '#dc2626', textAlign: 'right' }}>
                      ({fmtUSD(totalExpenses)})
                    </td>
                    <td style={{ padding: '8px 16px', fontSize: '12px', color: '#6b7280', textAlign: 'right', fontWeight: '600' }}>
                      {fmtPct(revenue > 0 ? (totalExpenses / revenue) * 100 : 0)}
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: netProfit >= 0 ? '#f0fdf4' : '#fef2f2', borderTop: '3px solid', borderTopColor: netProfit >= 0 ? '#16a34a' : '#dc2626' }}>
                    <td style={{ padding: '12px 16px', fontSize: '16px', fontWeight: '800', color: '#111827' }}>
                      = GANANCIA NETA
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '16px', fontWeight: '800', color: netProfit >= 0 ? '#16a34a' : '#dc2626', textAlign: 'right' }}>
                      {netProfit < 0 ? `(${fmtUSD(Math.abs(netProfit))})` : fmtUSD(netProfit)}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: netProfit >= 0 ? '#16a34a' : '#dc2626', textAlign: 'right', fontWeight: '700' }}>
                      {fmtPct(netMargin)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </SectionBody>

            {/* ══ SECCIÓN 3: GRÁFICAS DE DESEMPEÑO ══════════════════ */}
            <SectionHeader title="3. Gráficas de Desempeño" color="#1e3a8a" />
            <SectionBody>
              <div style={{ display: 'flex', gap: '24px', justifyContent: 'space-between' }}>
                {/* Bar chart: comparación */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '12px', textAlign: 'center' }}>
                    Distribución del Mes
                  </div>
                  <BarChart width={520} height={280} data={barCompareData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => fmtUSD(v)} />
                    <Bar dataKey="Valor" radius={[4, 4, 0, 0]}>
                      {barCompareData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </div>
                {/* Pie chart: distribución */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '12px', textAlign: 'center' }}>
                    Distribución de Resultados
                  </div>
                  {distData.length > 0 ? (
                    <PieChart width={520} height={280}>
                      <Pie
                        data={distData}
                        cx={260}
                        cy={130}
                        outerRadius={110}
                        innerRadius={55}
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                        labelLine={true}
                      >
                        {distData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmtUSD(v)} />
                    </PieChart>
                  ) : (
                    <div style={{ textAlign: 'center', color: '#6b7280', padding: '40px' }}>Sin datos</div>
                  )}
                </div>
              </div>
            </SectionBody>

            {/* ══ SECCIÓN 4: VENTAS POR TIENDA ═══════════════════════ */}
            <SectionHeader title="4. Ventas por Tienda" color="#1e40af" />
            <SectionBody>
              <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                {/* Bar chart */}
                <div>
                  <BarChart width={580} height={260} data={salesChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="tienda" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(v, n, p) => [fmtUSD(v), 'Ventas']}
                      labelFormatter={(l, payload) => payload?.[0]?.payload?.fullName || l}
                    />
                    <Bar dataKey="Ventas" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </div>
                {/* Table */}
                <div style={{ flex: 1 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <Th>Tienda</Th>
                        <Th align="right">Ventas (USD)</Th>
                        <Th align="right">Transacciones</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {(d?.salesByStore || []).map((s, i) => (
                        <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <Td>{s.store_name}</Td>
                          <Td align="right" bold color="#2563eb">{fmtUSD(s.total_sales)}</Td>
                          <Td align="right">{fmtNum(s.sales_count)}</Td>
                        </tr>
                      ))}
                      <tr style={{ backgroundColor: '#eff6ff', borderTop: '2px solid #bfdbfe' }}>
                        <Td bold>TOTAL</Td>
                        <Td align="right" bold color="#1d4ed8">
                          {fmtUSD((d?.salesByStore || []).reduce((s, r) => s + (r.total_sales || 0), 0))}
                        </Td>
                        <Td align="right" bold>
                          {fmtNum((d?.salesByStore || []).reduce((s, r) => s + (r.sales_count || 0), 0))}
                        </Td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </SectionBody>

            {/* ══ SECCIÓN 5: GASTOS POR CATEGORÍA ════════════════════ */}
            <SectionHeader title="5. Gastos por Categoría" color="#7c3aed" />
            <SectionBody>
              <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                {/* Pie chart */}
                <div style={{ width: '420px' }}>
                  {expCatData.length > 0 ? (
                    <>
                      <div style={{ fontSize: '11px', color: '#6b7280', textAlign: 'center', marginBottom: '4px' }}>
                        Gastos operativos (excluye Pagos a Proveedores)
                      </div>
                      <PieChart width={420} height={240}>
                        <Pie
                          data={expCatData}
                          cx={210}
                          cy={115}
                          outerRadius={105}
                          innerRadius={48}
                          dataKey="value"
                          paddingAngle={2}
                        >
                          {expCatData.map((e, i) => (
                            <Cell key={i} fill={e.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, name) => [fmtUSD(v), name]} />
                      </PieChart>
                      {/* Custom legend below chart */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: '8px' }}>
                        {expCatData.map((e, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#374151' }}>
                            <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: e.color, flexShrink: 0 }} />
                            {e.name}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div style={{ padding: '40px', color: '#6b7280' }}>Sin gastos registrados</div>
                  )}
                </div>
                {/* Table */}
                <div style={{ flex: 1 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <Th>Categoría</Th>
                        <Th align="right">Monto (USD)</Th>
                        <Th align="right">% del Total</Th>
                        <Th align="right">Registros</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {(d?.expensesByCategory || []).filter((e) => e.total_amount > 0).map((e, i) => (
                        <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <Td>
                            <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: CHART_COLORS[i % CHART_COLORS.length], marginRight: '6px' }} />
                            {getCategoryLabel(e.category)}
                          </Td>
                          <Td align="right" bold color="#7c3aed">{fmtUSD(e.total_amount)}</Td>
                          <Td align="right">{fmtPct(totalExpenses > 0 ? (e.total_amount / totalExpenses) * 100 : 0)}</Td>
                          <Td align="right">{fmtNum(e.expense_count)}</Td>
                        </tr>
                      ))}
                      <tr style={{ backgroundColor: '#f5f3ff', borderTop: '2px solid #ddd6fe' }}>
                        <Td bold>TOTAL GASTOS</Td>
                        <Td align="right" bold color="#6d28d9">{fmtUSD(totalExpenses)}</Td>
                        <Td align="right" bold>100%</Td>
                        <Td align="right" bold>
                          {fmtNum((d?.expensesByCategory || []).reduce((s, e) => s + (e.expense_count || 0), 0))}
                        </Td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </SectionBody>

            {/* ══ SECCIÓN 6: MÉTODOS DE PAGO ═════════════════════════ */}
            <SectionHeader title="6. Métodos de Pago" color="#0891b2" />
            <SectionBody>
              {payChartData.length > 0 ? (
                <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                  {/* Bar chart */}
                  <div>
                    <BarChart
                      width={560}
                      height={280}
                      data={payChartData}
                      layout="vertical"
                      margin={{ left: 10, right: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} tick={{ fontSize: 10 }} />
                      <YAxis dataKey="metodo" type="category" width={120} tick={{ fontSize: 10 }} />
                      <Tooltip
                        formatter={(v, n, p) => [fmtUSD(v), p.payload?.fullLabel || 'Monto']}
                      />
                      <Bar dataKey="Monto" fill="#0891b2" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </div>
                  {/* Table */}
                  <div style={{ flex: 1 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <Th>Método</Th>
                          <Th align="right">Monto (USD)</Th>
                          <Th align="right">% Total</Th>
                          <Th align="right">Transac.</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {payTotals.map((p, i) => {
                          const isUnclassified = p.method === '__unclassified__';
                          const tdStyle = { padding: '7px 12px', fontSize: '13px', borderBottom: '1px solid #f3f4f6', color: '#9ca3af', fontStyle: 'italic' };
                          if (isUnclassified) {
                            return (
                              <tr key={i} style={{ backgroundColor: '#fafafa' }}>
                                <td style={tdStyle}>{p.label}</td>
                                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>{fmtUSD(p.usd)}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtPct(payGrandTotal > 0 ? (p.usd / payGrandTotal) * 100 : 0)}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>—</td>
                              </tr>
                            );
                          }
                          return (
                            <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                              <Td>{p.label}</Td>
                              <Td align="right" bold color="#0891b2">{fmtUSD(p.usd)}</Td>
                              <Td align="right">{fmtPct(payGrandTotal > 0 ? (p.usd / payGrandTotal) * 100 : 0)}</Td>
                              <Td align="right">{fmtNum(p.count)}</Td>
                            </tr>
                          );
                        })}
                        <tr style={{ backgroundColor: '#ecfeff', borderTop: '2px solid #a5f3fc' }}>
                          <Td bold>TOTAL</Td>
                          <Td align="right" bold color="#0e7490">
                            {fmtUSD(payGrandTotal)}
                          </Td>
                          <Td align="right" bold>100%</Td>
                          <Td align="right" bold>
                            {fmtNum(payTotalsRaw.reduce((s, p) => s + p.count, 0))}
                          </Td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#6b7280', padding: '16px', textAlign: 'center' }}>
                  No hay datos de métodos de pago para este período.
                </div>
              )}
            </SectionBody>

            {/* ══ SECCIÓN 7: UNIDADES VENDIDAS ═══════════════════════ */}
            <SectionHeader title="7. Unidades Vendidas por Tienda" color="#16a34a" />
            <SectionBody noPadding>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <Th>Tienda</Th>
                    <Th align="right">Unidades Vendidas</Th>
                    <Th align="right">Transacciones</Th>
                    <Th align="right">Ventas (USD)</Th>
                    <Th align="right">Ticket Promedio</Th>
                  </tr>
                </thead>
                <tbody>
                  {(d?.unitsSold?.by_store || []).map((s, i) => (
                    <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                      <Td>{s.store_name}</Td>
                      <Td align="right" bold color="#16a34a">{fmtNum(s.total_units)}</Td>
                      <Td align="right">{fmtNum(s.total_transactions)}</Td>
                      <Td align="right">{fmtUSD(s.total_sales_usd)}</Td>
                      <Td align="right">
                        {fmtUSD(s.total_transactions > 0 ? s.total_sales_usd / s.total_transactions : 0)}
                      </Td>
                    </tr>
                  ))}
                  {(d?.unitsSold?.by_store || []).length > 0 && (() => {
                    const bs = d.unitsSold.by_store;
                    const tu = bs.reduce((s, r) => s + (r.total_units || 0), 0);
                    const tt = bs.reduce((s, r) => s + (r.total_transactions || 0), 0);
                    const ts = bs.reduce((s, r) => s + (r.total_sales_usd || 0), 0);
                    return (
                      <tr style={{ backgroundColor: '#f0fdf4', borderTop: '2px solid #bbf7d0' }}>
                        <Td bold>TOTAL</Td>
                        <Td align="right" bold color="#15803d">{fmtNum(tu)}</Td>
                        <Td align="right" bold>{fmtNum(tt)}</Td>
                        <Td align="right" bold>{fmtUSD(ts)}</Td>
                        <Td align="right" bold>{fmtUSD(tt > 0 ? ts / tt : 0)}</Td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </SectionBody>

            {/* ══ SECCIÓN 8: COMPRAS DE INVENTARIO ═══════════════════ */}
            <SectionHeader title="8. Compras de Inventario por Tienda" color="#d97706" />
            <SectionBody noPadding>
              {invStoreRows.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <Th>Tienda</Th>
                      <Th align="right">Unidades Compradas</Th>
                      <Th align="right">Nº Facturas</Th>
                      <Th align="right">Costo Total (USD)</Th>
                      <Th align="right">Costo en Bs.</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {invStoreRows.map((r, i) => (
                      <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                        <Td>{r.storeName}</Td>
                        <Td align="right" bold color="#d97706">{fmtNum(r.units)}</Td>
                        <Td align="right">{fmtNum(r.count)}</Td>
                        <Td align="right">{fmtUSD(r.cost)}</Td>
                        <Td align="right">{fmtVES(r.cost, exchangeRate)}</Td>
                      </tr>
                    ))}
                    <tr style={{ backgroundColor: '#fffbeb', borderTop: '2px solid #fde68a' }}>
                      <Td bold>TOTAL</Td>
                      <Td align="right" bold color="#b45309">{fmtNum(totalInvUnits)}</Td>
                      <Td align="right" bold>{fmtNum(invStoreRows.reduce((s, r) => s + r.count, 0))}</Td>
                      <Td align="right" bold>{fmtUSD(totalInventory)}</Td>
                      <Td align="right" bold>{fmtVES(totalInventory, exchangeRate)}</Td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: '20px', color: '#6b7280', textAlign: 'center' }}>
                  No hay compras de inventario registradas en este período.
                </div>
              )}
            </SectionBody>

            {/* ══ SECCIÓN 9: VALOR DEL INVENTARIO ════════════════════ */}
            <SectionHeader title="9. Valor del Inventario en Existencia" color="#0f766e" />
            <SectionBody>
              {invValTotals ? (
                <>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                    Existencias según Tecnodroid al <strong>{inventoryVal.snapshot_date}</strong>
                    {inventoryVal.snapshot_date !== endDate && inventoryVal.is_current ? ' (valor actual; el mes aún no cierra)' : ''}
                    {' · '}Tasa {fmtNum(inventoryVal.exchange_rate)} Bs/USD · {fmtNum(invValTotals.units)} unidades en {fmtNum(invValTotals.skus)} productos
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    <MetricCard
                      label="Inventario al costo"
                      value={fmtUSD(invValTotals.cost_usd)}
                      sub={`Bs. ${fmtNum(Math.round(invValTotals.cost_ves))}`}
                      color="#0f766e"
                      bg="#f0fdfa"
                    />
                    <MetricCard
                      label="Inventario a precio de venta"
                      value={fmtUSD(invValTotals.sale_usd)}
                      sub={`Bs. ${fmtNum(Math.round(invValTotals.sale_ves))}`}
                      color="#2563eb"
                      bg="#eff6ff"
                    />
                    <MetricCard
                      label="Utilidad potencial"
                      value={fmtUSD(invValTotals.profit_usd)}
                      sub={`Bs. ${fmtNum(Math.round(invValTotals.profit_ves))} · margen ${fmtPct(invValTotals.margin_pct)}`}
                      color="#16a34a"
                      bg="#f0fdf4"
                    />
                  </div>
                  {invValRows.length > 1 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <Th>Tienda</Th>
                          <Th align="right">Unidades</Th>
                          <Th align="right">Al costo (USD)</Th>
                          <Th align="right">Al costo (Bs.)</Th>
                          <Th align="right">A precio venta (USD)</Th>
                          <Th align="right">Utilidad (USD)</Th>
                          <Th align="right">Margen</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {invValRows.map((r, i) => (
                          <tr key={r.store_id} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                            <Td>{r.store_name}</Td>
                            <Td align="right">{fmtNum(r.units)}</Td>
                            <Td align="right" bold color="#0f766e">{fmtUSD(r.cost_usd)}</Td>
                            <Td align="right">Bs. {fmtNum(Math.round(r.cost_ves))}</Td>
                            <Td align="right">{fmtUSD(r.sale_usd)}</Td>
                            <Td align="right" color="#16a34a">{fmtUSD(r.profit_usd)}</Td>
                            <Td align="right">{fmtPct(r.margin_pct)}</Td>
                          </tr>
                        ))}
                        <tr style={{ backgroundColor: '#f0fdfa', borderTop: '2px solid #99f6e4' }}>
                          <Td bold>TOTAL</Td>
                          <Td align="right" bold>{fmtNum(invValTotals.units)}</Td>
                          <Td align="right" bold color="#0f766e">{fmtUSD(invValTotals.cost_usd)}</Td>
                          <Td align="right" bold>Bs. {fmtNum(Math.round(invValTotals.cost_ves))}</Td>
                          <Td align="right" bold>{fmtUSD(invValTotals.sale_usd)}</Td>
                          <Td align="right" bold color="#16a34a">{fmtUSD(invValTotals.profit_usd)}</Td>
                          <Td align="right" bold>{fmtPct(invValTotals.margin_pct)}</Td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </>
              ) : (
                <div style={{ color: '#6b7280', textAlign: 'center' }}>
                  No hay valoración de inventario disponible para este período (los snapshots de Tecnodroid comienzan el 12/09/2026).
                </div>
              )}
            </SectionBody>

            {/* ══ PIE DE PÁGINA ═════════════════════════════════════ */}
            <div
              style={{
                borderTop: '2px solid #e5e7eb',
                marginTop: '8px',
                paddingTop: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '11px',
                color: '#9ca3af',
              }}
            >
              <span>Farmavida — Gestión Financiera</span>
              <span>Reporte de {monthLabel}</span>
              <span>Generado el {new Date().toLocaleDateString('es-VE')}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyReportPDF;
